import { unsafeCSS, html } from "lit";
import { property, query } from "lit/decorators.js";
import { FormControlMixin } from "../../utilities/mixins/form-control.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { requiredValidator } from "../../utilities/validators.js";
import { watch } from "../../utilities/watch.js";
import { LeafElement } from "../leaf-element.js";
const styles = "/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  --leaf-checkbox-box-shadow-color: var(--leaf-color-border-active-outline);\n  font-family: var(--leaf-typography-label-small-font-family);\n  font-size: var(--leaf-typography-label-small-font-size);\n  font-weight: var(--leaf-typography-label-small-font-weight);\n  line-height: var(--leaf-typography-label-small-line-height);\n  -webkit-text-decoration: var(--leaf-typography-label-small-text-decoration);\n          text-decoration: var(--leaf-typography-label-small-text-decoration);\n  text-transform: var(--leaf-typography-label-small-text-transform);\n  display: block;\n}\n\n:host(:focus),\n:host(:focus-visible),\n:host(:focus-within) {\n  outline: 0;\n}\n:host(:focus) .leaf-c-checkbox__outer,\n:host(:focus-visible) .leaf-c-checkbox__outer,\n:host(:focus-within) .leaf-c-checkbox__outer {\n  box-shadow: 0 0 0 calc(var(--leaf-border-width-button-default) * 1) var(--leaf-color-border-knockout), 0 0 0 calc(var(--leaf-border-width-button-default) * 2) var(--leaf-checkbox-box-shadow-color);\n}\n\n:host([invalid]:focus) .leaf-c-checkbox__outer,\n:host([invalid]:focus-visible) .leaf-c-checkbox__outer,\n:host([invalid]:focus-within) .leaf-c-checkbox__outer {\n  --leaf-checkbox-box-shadow-color: var(--leaf-color-border-error);\n}\n\n:host(:disabled),\n:host([disabled]) {\n  pointer-events: none;\n}\n:host(:disabled) .leaf-c-checkbox__outer,\n:host(:disabled) .leaf-c-checkbox__inner,\n:host([disabled]) .leaf-c-checkbox__outer,\n:host([disabled]) .leaf-c-checkbox__inner {\n  background-color: var(--leaf-color-bg-disabled);\n  border-color: var(--leaf-color-border-disabled);\n}\n:host(:disabled) leaf-icon[name=check],\n:host([disabled]) leaf-icon[name=check] {\n  color: var(--leaf-color-content-disabled);\n}\n\n.leaf-c-checkbox__wrapper {\n  display: flex;\n  gap: var(--leaf-spacing-8);\n}\n\n.leaf-c-checkbox__label {\n  align-self: center;\n}\n\n.leaf-c-checkbox__outer,\n.leaf-c-checkbox__inner {\n  align-items: center;\n  display: flex;\n  border: var(--leaf-border-width-default) solid var(--leaf-color-border-strong);\n  border-radius: var(--leaf-border-radius-small);\n  height: var(--leaf-spacing-24);\n  justify-content: center;\n  min-width: var(--leaf-spacing-24);\n  width: var(--leaf-spacing-24);\n}\n\n.leaf-c-checkbox__inner {\n  background-color: var(--leaf-color-content-brand);\n}\n\nleaf-icon[name=check] {\n  --icon-height: var(--leaf-spacing-24);\n  color: var(--leaf-color-content-knockout);\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const _LeafCheckbox = class _LeafCheckbox extends FormControlMixin(LeafElement) {
  constructor() {
    super(...arguments);
    this.internals = this.attachInternals();
    this.validation = new ValidationController(this, {
      validators: [requiredValidator],
      nativeTarget: () => this
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.internals.role = "checkbox";
    this.setFormControlElement(this);
    this.validation.validate(this.checked ? this.value : void 0);
    this.addEventListener("click", this.onClick);
    this.addEventListener("keydown", this.onKeydown);
    if (!this.hasAttribute("tabindex")) {
      this.tabIndex = 0;
    }
  }
  handleStateChange() {
    this.internals.ariaInvalid = this.invalid ? "true" : "false";
    this.internals.ariaChecked = this.checked ? "true" : "false";
    this.internals.ariaDisabled = this.disabled ? "true" : "false";
    if (this.invalid) {
      this.internals.states.add("--invalid");
    } else {
      this.internals.states.delete("--invalid");
    }
    if (this.checked) {
      this.internals.states.add("--checked");
    } else {
      this.internals.states.delete("--checked");
    }
    if (this.disabled) {
      this.internals.states.add("--disabled");
    } else {
      this.internals.states.delete("--disabled");
    }
  }
  render() {
    return html`
      <div class="leaf-c-checkbox__wrapper">
        <div class="leaf-c-checkbox__outer" part="checkbox-outer">
          ${this.checked ? html`<div class="leaf-c-checkbox__inner"><leaf-icon set="system" name="check"></leaf-icon></div>` : ""}
        </div>
        <span class="leaf-c-checkbox__label">
          <slot></slot>
        </span>
      </div>
    `;
  }
  onClick(event) {
    if (event.target === this) {
      this.onToggle(event);
      this.emit("leaf-checkbox:change", { detail: event.target });
    }
  }
  onKeydown(event) {
    if (this.disabled) {
      return;
    }
    if (["Enter", " "].includes(event.key)) {
      event.preventDefault();
      this.checkboxWrapper.click();
    }
  }
  onToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (!this.value) {
      this.value = "on";
    } else if (this.value === "on") {
      this.value = "";
    }
    this.checked = !this.checked;
    this.validation.validate(this.checked ? this.value : void 0);
  }
};
_LeafCheckbox.styles = unsafeCSS(styles);
_LeafCheckbox.formAssociated = true;
let LeafCheckbox = _LeafCheckbox;
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckbox.prototype, "checked", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckbox.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckbox.prototype, "invalid", 2);
__decorateClass([
  property()
], LeafCheckbox.prototype, "value", 2);
__decorateClass([
  query(".leaf-c-checkbox__wrapper")
], LeafCheckbox.prototype, "checkboxWrapper", 2);
__decorateClass([
  watch(["disabled", "invalid", "checked"])
], LeafCheckbox.prototype, "handleStateChange", 1);
export {
  LeafCheckbox
};
//# sourceMappingURL=checkbox.component.js.map
